import DiffMatchPatch from 'diff-match-patch';

import JsonDiff from '@/components/Diff/JsonDiff';
import { Message } from '@/components/Message';
import { DiffChangeType } from '@/types/diff';
import { MessageContentPart } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { getDiffColorFromChangeType } from '../utils';
import { MessageDiff } from './types';

export const RenderDiff = ({
  diffs,
  messageType,
}: {
  diffs: MessageDiff[];
  messageType?: DiffChangeType[];
}) => {
  const renderText = (
    changeType: number,
    text: string | MessageContentPart[]
  ) => {
    if (typeof text === 'string') return text;
    return (
      <div className="pt-2">
        <Message
          content={text}
          className={getDiffColorFromChangeType(changeType)}
        />
      </div>
    );
  };

  const renderMessageType = (diff: DiffChangeType[]) => {
    // map over the array and render the text
    const messageTypeParts = diff.map(([changeType, text], index) => {
      return (
        <span
          className={cn(
            'leading-[1.6em] focus:outline-none',
            getDiffColorFromChangeType(changeType)
          )}
          key={index}
        >
          {renderText(changeType, text)}
        </span>
      );
    });

    return <div className="flex flex-row">{messageTypeParts}</div>;
  };

  return (
    <DiffMessage
      title={messageType ? renderMessageType(messageType) : undefined}
    >
      <div className="whitespace-pre-wrap text-sm font-[Fira_Code] focus:outline-none">
        {diffs.length === 0 || (diffs.length === 1 && diffs[0][1] === '') ? (
          <div className="mt-1 w-fit rounded-md bg-tertiary px-2 py-1 font-mono text-xs">
            No content
          </div>
        ) : (
          diffs.map(([changeType, text], index) => {
            if (changeType === DiffMatchPatch.DIFF_EQUAL)
              return (
                <span className="focus:outline-none" key={index}>
                  {renderText(changeType, text)}
                </span>
              );
            return (
              <span
                className={cn(
                  'leading-[1.6em] focus:outline-none',
                  getDiffColorFromChangeType(changeType)
                )}
                key={index}
              >
                {renderText(changeType, text)}
              </span>
            );
          })
        )}
      </div>
    </DiffMessage>
  );
};

export const JsonMessageDiff = ({
  diffs,
  title,
}: {
  diffs: DiffChangeType[];
  title: string;
}) => {
  return (
    <DiffMessage title={title}>
      <div className="text-xs">
        <JsonDiff diffs={diffs} />
      </div>
    </DiffMessage>
  );
};

export const DiffMessage = ({
  title,
  children,
}: {
  title?: string | JSX.Element;
  children: JSX.Element;
}) => {
  return (
    <div className="flex flex-col rounded-md border border-secondary px-4 py-3.5 break-anywhere">
      {title && (
        <div className="mb-0.5 h-5 text-sm font-medium uppercase text-tertiary">
          {title}
        </div>
      )}
      {children}
    </div>
  );
};
